import React, {useEffect, useState} from 'react';
import Table from '../../common/table/Table';
import {Column} from "../../common/table/TableSection";
import DateRangeFilter from '../../common/table/DateRangeFilter';
import {useNavigate} from "react-router-dom";
import styles from "./AdminWorkerRequestTable.module.css";
import {GetTaxInvoiceTableResponseData} from "../../../types/admin";
import {InvoiceSort, InvoiceTarget} from "../../../types/contract";
import {adminTaxInvoiceTableAPI} from "../../../services/admin";
import Button from "../../common/button/Button";
import CreateTaxInvoicePopUp from "../../../views/admin/popup/CreateTaxInvoicePopUp";

interface DateRange {
    start: Date | null;
    end: Date | null;
}

interface DateRanges {
    issue: DateRange;
    create: DateRange;
}

//어드민 세금 계산서 테이블
//세금계산서 API 사용 시 연결 및 수정 필요
const AdminTaxInvoiceTable = () => {
    const navigate = useNavigate();
    const [data, setData] = useState<GetTaxInvoiceTableResponseData[]>([]);
    const [loading, setLoading] = useState(true);
    const [isCreatePopupOpen, setIsCreatePopupOpen] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [pendingSearchTerm, setPendingSearchTerm] = useState('');
    const [isResetting, setIsResetting] = useState(false);
    const [filters, setFilters] = useState<Record<string, any>>({
        status: ['임시저장', '발행', '전송전', '전송성공', '전송실패', '발행취소'],
        issue: 'ALL',
        create: 'ALL',
        min: '',
        max: ''
    });
    // 필터 전체 초기화
    const resetAllFilters = () => {
        setIsResetting(true);

        // 필터 초기화
        setFilters({
            status: ['임시저장', '발행', '전송전', '전송성공', '전송실패', '발행취소'],
            issue: 'ALL',
            create: 'ALL',
            min: '',
            max: ''
        });

        // 날짜 범위 초기화
        setDateRanges({
            create: { start: null, end: null },
            issue: { start: null, end: null }
        });

        // 검색 관련 초기화
        setSearchTerm('');
        setPendingSearchTerm('');

        // 페이지 초기화
        setPage(1);

        // 정렬 초기화
        setSort({
            field: 'companyName',
            direction: 'asc'
        });

        // 다음 렌더링 사이클에서 리셋 상태 해제
        setTimeout(() => setIsResetting(false), 0);
    };
    //테이블 로우 선택 시
    const handleRowClick = (row: GetTaxInvoiceTableResponseData) => {
        navigate(`/admin/billing/tax/${row.taxInvoiceId}`);
    };

    //날짜 필터 값
    const [dateRanges, setDateRanges] = useState<DateRanges>({
        issue: {start: null, end: null},
        create: {start: null, end: null}
    });

    //정렬
    const [sort, setSort] = useState<{ field: string | null, direction: 'asc' | 'desc' | null }>({
        field: 'companyName',
        direction: 'asc'
    });

    //컬럼
    const columns: Column<GetTaxInvoiceTableResponseData>[] = [
        {
            key: 'No',
            header: '결제 번호',
            width: '8%'
        },
        {
            key: 'companyName',
            header: '회사명',
            width: '15%',
            sortable: true
        },
        {
            key: 'createdAt',
            header: '작성일',
            width: '20%',
            sortable: true
        },
        {
            key: 'issuedAt',
            header: '발행일시',
            width: '20%',
            sortable: true,
            render: value => formatDateTime(value)
        },
        {
            key: 'status',
            header: '상태',
            width: '15%'
        },
        {
            key: 'total',
            header: '대금 총액',
            width: '15%',
            sortable: true,
            render: value => value.toLocaleString() + "원"
        }
    ];

    const formatDateForAPI = (date: Date | string | null): string => {
        if (!date) return '';
        const dateObj = date instanceof Date ? date : new Date(date);

        if (isNaN(dateObj.getTime())) {
            return '-';
        }
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const formatDateRangeForAPI = (start: Date | null, end: Date | null): string | null => {
        if (!start && !end) return 'ALL';
        if (!end) return `${formatDateForAPI(start)}~`;
        return `${formatDateForAPI(start)}~${formatDateForAPI(end)}`;
    };

    const handleDateRangeChange = (key: string, start: Date | null, end: Date | null) => {
        if (isResetting) return;

        setDateRanges(prev => ({
            ...prev,
            [key]: {start, end}
        }));

        const dateRangeString = formatDateRangeForAPI(start, end);
        setFilters(prev => ({
            ...prev,
            [key]: dateRangeString
        }));

        setPage(1);
    };

    //작업자 추가 요청 상태 필터
    const statusFilterOptions = [
        {label: '임시저장', value: '임시저장'},
        {label: '발행', value: '발행'},
        {label: '전송전', value: '전송전'},
        {label: '전송성공', value: '전송성공'},
        {label: '전송실패', value: '전송실패'},
        {label: '발행취소', value: '발행취소'}
    ];

    //날짜 데이터 형식 변환
    const formatDateTime = (date: Date | null) => {
        if(!date) return '-';
        return new Date(date).toLocaleString('ko-KR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        })
    }

    //테이블 필터 설정
    const tableFilters = [
        {
            key: 'status',
            label: '처리 상태',
            options: statusFilterOptions,
            multiple: true,
            value: filters.status,
            component: undefined
        },
        {
            key: 'total',
            label: '대금 총액',
            component: (
                <div className={styles.numberRangeFilter}>
                    <span>대금 총액: </span>
                    <div className={styles.rangeInput}>
                        <input
                            type="number"
                            min="0"
                            placeholder="최소"
                            value={filters.min === 0 ? '' : filters.min}
                            onChange={(e) => handleFilterChange('min', e.target.value)}
                            className={styles.numberInput}
                            title="최소 총액"
                        />
                        <span className={styles.rangeSeparator}>~</span>
                        <input
                            type="number"
                            min="0"
                            placeholder="최대"
                            value={filters.max}
                            onChange={(e) => handleFilterChange('max', e.target.value)}
                            className={styles.numberInput}
                            title="최대 총액"
                        />
                    </div>
                </div>
            ),
            options: [],
            multiple: false,
            value: null
        },
        {
            key: 'create',
            label: '생성일',
            component: (
                <DateRangeFilter
                    label="생성일"
                    startDate={dateRanges.create.start}
                    endDate={dateRanges.create.end}
                    onDateChange={(start, end) => handleDateRangeChange('create', start, end)}
                    isOpen={false}
                    onToggle={() => {
                    }}
                />
            ),
            options: [],
            multiple: false,
            value: null
        },
        {
            key: 'issue',
            label: '발행일',
            component: (
                <DateRangeFilter
                    label="발행일"
                    startDate={dateRanges.issue.start}
                    endDate={dateRanges.issue.end}
                    onDateChange={(start, end) => handleDateRangeChange('issue', start, end)}
                    isOpen={false}
                    onToggle={() => {
                    }}
                />
            ),
            options: [],
            multiple: false,
            value: null
        }
    ];

    //정렬 기준 설정
    const getSortParam = (field: string | null, direction: 'asc' | 'desc' | null): InvoiceSort => {
        if (!field || !direction) return InvoiceSort.CREATE_DESC;
        const sortMap: Record<string, Record<string, InvoiceSort>> = {
            companyName: {
                asc: InvoiceSort.COMPANY_ASC,
                desc: InvoiceSort.COMPANY_DESC
            },
            createdAt: {
                asc: InvoiceSort.CREATE_ASC,
                desc: InvoiceSort.CREATE_DESC
            },
            issuedAt: {
                asc: InvoiceSort.ISSUE_ASC,
                desc: InvoiceSort.ISSUE_DESC
            },
            total: {
                asc: InvoiceSort.TOTAL_ASC,
                desc: InvoiceSort.TOTAL_DESC
            }
        };
        return sortMap[field]?.[direction] || InvoiceSort.CREATE_DESC;
    };

    //검색
    const handleSearch = () => {
        setSearchTerm(pendingSearchTerm);
        setPage(1);
    };

    //엔터키 사용 검색
    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            //API 추가
            const response = await adminTaxInvoiceTableAPI({
                page: page - 1,
                sort: getSortParam(sort.field, sort.direction),
                target: searchTerm,
                search: searchTerm,
                create: filters.create,
                issue: filters.issue,
                min: filters.min,
                max: filters.max
            });

            setData(response.content);
            setTotalPages(Math.ceil(response.total / 7));
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [page, searchTerm, sort, filters]);

    //검색 대상 설정
    const searchOptions = [
        {value: InvoiceTarget.COMPANY_NAME, label: '회사명'}
    ];

    //필터값 변경
    const handleFilterChange = (key: string, value: any) => {
        setFilters(prev => {
            if (key === 'status') {
                const newStatuses = value;
                if (newStatuses.length === 0) {
                    return prev;
                }
                return {...prev, status: newStatuses};
            }

            if (key === 'min' || key === 'max') {
                if (value === '' || /^\d+$/.test(value)) {
                    const numberValue = value === '' ? '' : parseInt(value);
                    if (key === 'min' && prev.max !== '' && numberValue > parseInt(prev.max)) {
                        return {...prev, min: numberValue, max: numberValue};
                    }
                    if (key === 'max' && prev.min !== '' && numberValue !== '' && numberValue < parseInt(prev.min)) {
                        return {...prev, min: numberValue, max: numberValue};
                    }
                    return {...prev, [key]: numberValue};
                }
                return prev;
            }

            return {...prev, [key]: value};
        });
        setPage(1);
    };

    //정렬 변경
    const handleSortChange = (field: string | null, direction: 'asc' | 'desc' | null) => {
        setSort({field, direction});
    };

    return (
        <div className={styles.container}>
            <div className={styles.searchContainer}>
                <div className={styles.titleContainer}>
                    <div className={styles.padding}/>
                    <h3>세금계산서</h3>
                    <Button
                        variant="primary"
                        onClick={() => setIsCreatePopupOpen(true)}
                        size={"small"}
                        className={styles.mobileButton}
                    >
                        발행
                    </Button>
                </div>
                <div className={styles.searchInputContainer}>
                    <div>
                        <select
                            value={"회사명"}
                            className={styles.searchTypeSelect}
                            title="검색 유형 선택"
                        >
                            {searchOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.searchInputWrapper}>
                        <input
                            type="text"
                            placeholder="검색어를 입력하세요"
                            value={pendingSearchTerm}
                            onChange={(e) => setPendingSearchTerm(e.target.value)}
                            onKeyPress={handleKeyPress}
                            className={styles.searchInput}
                            title="검색어 입력"
                        />
                        <Button
                            onClick={handleSearch}
                            className={styles.searchButton}
                            variant="primary"
                            size={"small"}
                        >
                            검색
                        </Button>
                        <Button
                            onClick={() => setIsCreatePopupOpen(true)}
                            className={styles.pcButton}
                            size={"small"}
                            variant="primary"
                        >
                            발행
                        </Button>
                    </div>
                </div>
                </div>
                <Table
                    columns={columns}
                    data={data}
                    loading={loading}
                    size="medium"
                    filters={tableFilters}
                    showPagination={true}
                    page={page}
                    pageSize={7}
                    totalItems={totalPages * 7}
                    onPageChange={setPage}
                    onFilterChange={handleFilterChange}
                    onSortChange={handleSortChange}
                    onRowClick={handleRowClick}
                    onReset={resetAllFilters}
                />
                <CreateTaxInvoicePopUp
                    isOpen={isCreatePopupOpen}
                    onClose={() => {
                        setIsCreatePopupOpen(false);
                        fetchData();
                    }}
                />
            </div>
            );
            };

            export default AdminTaxInvoiceTable;